import { useState, useRef, useEffect } from 'react'
import { useBreakpoint } from '@/hooks/useBreakpoint'

const MONTHS_FULL  = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
const MONTHS_SHORT = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
const DAYS_SHORT   = ['L','M','X','J','V','S','D']

function isoMonday(date: Date): Date {
  const d = new Date(date); d.setHours(0, 0, 0, 0)
  const dow = d.getDay()
  d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1))
  return d
}

/** Días de calendario entre hoy y `date`.
 *
 *  Se cuenta sobre medianoches en UTC y no dividiendo milisegundos: en la semana
 *  del cambio de hora, `(b - a) / 864e5` da 6,96 y trunca a 6. */
function dayOffsetFromDate(date: Date): number {
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0)
  const a = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
  const b = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  return Math.round((b - a) / 864e5)
}

function dateFromDayOffset(offset: number): Date {
  const d = new Date(); d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + offset)
  return d
}

function weekOffsetFromDate(date: Date): number {
  const todayMon  = isoMonday(new Date())
  const targetMon = isoMonday(date)
  return Math.round((targetMon.getTime() - todayMon.getTime()) / (7 * 864e5))
}

function mondayFromWeekOffset(offset: number): Date {
  const mon = isoMonday(new Date())
  mon.setDate(mon.getDate() + offset * 7)
  return mon
}

function yearMonthFromOffset(offset: number): { year: number; month: number } {
  const today = new Date()
  const total  = today.getFullYear() * 12 + today.getMonth() + offset
  return { year: Math.floor(total / 12), month: total % 12 }
}

function monthOffsetFromYM(year: number, month: number): number {
  const today = new Date()
  return (year - today.getFullYear()) * 12 + (month - today.getMonth())
}

function yearFromOffset(offset: number): number {
  return new Date().getFullYear() + offset
}

function yearOffsetFromYear(year: number): number {
  return year - new Date().getFullYear()
}

function buildCalGrid(year: number, month: number): (Date | null)[][] {
  const first   = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0).getDate()
  const startDow = first.getDay() === 0 ? 6 : first.getDay() - 1
  const cells: (Date | null)[] = Array(startDow).fill(null)
  for (let d = 1; d <= lastDay; d++) cells.push(new Date(year, month, d))
  while (cells.length % 7) cells.push(null)
  const rows: (Date | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
  return rows
}

interface Props {
  /** `day` lo usa el cuadro de muelles, que se mira día a día. */
  mode: 'day' | 'week' | 'month' | 'year'
  value: number
  onChange: (offset: number) => void
  label: string
  dark: boolean
  btnClass: string
}

export default function DateNavPicker({ mode, value, onChange, label, dark, btnClass }: Props) {
  const { isMobile } = useBreakpoint()
  const [open, setOpen]       = useState(false)
  const [calYear, setCalYear] = useState(0)
  const [calMonth, setCalMonth] = useState(0)
  const ref = useRef<HTMLDivElement>(null)

  // Sync display calendar to current value when opening
  useEffect(() => {
    if (!open) return
    if (mode === 'day') {
      const d = dateFromDayOffset(value)
      setCalYear(d.getFullYear()); setCalMonth(d.getMonth())
    } else if (mode === 'week') {
      const mon = mondayFromWeekOffset(value)
      setCalYear(mon.getFullYear()); setCalMonth(mon.getMonth())
    } else if (mode === 'year') {
      setCalYear(yearFromOffset(value))
    } else {
      setCalYear(yearMonthFromOffset(value).year)
    }
  }, [open, value, mode])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  const today    = new Date(); today.setHours(0, 0, 0, 0)
  const todayMon = isoMonday(today)
  const selMon   = mondayFromWeekOffset(value)

  const popBg  = dark ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'
  const txt    = dark ? 'text-slate-100' : 'text-slate-800'
  const muted  = dark ? 'text-slate-400' : 'text-slate-500'
  const bdrDiv = dark ? 'border-white/8' : 'border-slate-100'
  const navBtn = `flex items-center justify-center w-6 h-6 rounded text-[13px] transition-colors ${
    dark ? 'hover:bg-white/10 text-slate-300' : 'hover:bg-slate-100 text-slate-500'
  }`

  function DayPicker() {
    const rows = buildCalGrid(calYear, calMonth)
    const sel  = dateFromDayOffset(value)
    const prevM = () => calMonth === 0  ? (setCalYear(y => y-1), setCalMonth(11)) : setCalMonth(m => m-1)
    const nextM = () => calMonth === 11 ? (setCalYear(y => y+1), setCalMonth(0))  : setCalMonth(m => m+1)

    return (
      <div className="p-3 w-64">
        <div className="flex items-center justify-between mb-2">
          <button onClick={prevM} className={navBtn}>‹</button>
          <span className={`text-[12px] font-bold ${txt}`}>{MONTHS_FULL[calMonth]} {calYear}</span>
          <button onClick={nextM} className={navBtn}>›</button>
        </div>

        <div className="grid grid-cols-7 mb-1">
          {DAYS_SHORT.map(d => (
            <div key={d} className={`text-center text-[10px] font-bold py-0.5 ${muted}`}>{d}</div>
          ))}
        </div>

        {rows.map((row, ri) => (
          <div key={ri} className="grid grid-cols-7 mb-0.5">
            {row.map((d, ci) => {
              if (d === null) return <div key={ci} />
              const isSel   = d.getTime() === sel.getTime()
              const isToday = d.getTime() === today.getTime()
              const isThisMonth = d.getMonth() === calMonth
              return (
                <button
                  key={ci}
                  onClick={() => { onChange(dayOffsetFromDate(d)); setOpen(false) }}
                  className={`py-1 text-[11px] rounded transition-colors ${
                    isSel        ? 'bg-sky-500 text-white font-bold'
                    : isToday    ? (dark ? 'ring-1 ring-sky-400 text-sky-400 font-bold' : 'ring-1 ring-sky-400 text-sky-600 font-bold')
                    : !isThisMonth ? `${muted} ${dark ? 'hover:bg-white/6' : 'hover:bg-slate-50'}`
                    : `${txt} ${dark ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`
                  }`}
                >{d.getDate()}</button>
              )
            })}
          </div>
        ))}

        <div className={`mt-2 pt-2 border-t ${bdrDiv} flex justify-center`}>
          <button
            onClick={() => { onChange(0); setOpen(false) }}
            className={`text-[11px] font-semibold px-3 py-0.5 rounded transition-colors ${
              dark ? 'text-sky-400 hover:bg-white/8' : 'text-sky-600 hover:bg-sky-50'
            }`}
          >Hoy</button>
        </div>
      </div>
    )
  }

  function WeekPicker() {
    const rows = buildCalGrid(calYear, calMonth)
    const prevM = () => calMonth === 0  ? (setCalYear(y => y-1), setCalMonth(11)) : setCalMonth(m => m-1)
    const nextM = () => calMonth === 11 ? (setCalYear(y => y+1), setCalMonth(0))  : setCalMonth(m => m+1)

    return (
      <div className="p-3 w-64">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-2">
          <button onClick={prevM} className={navBtn}>‹</button>
          <span className={`text-[12px] font-bold ${txt}`}>{MONTHS_FULL[calMonth]} {calYear}</span>
          <button onClick={nextM} className={navBtn}>›</button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS_SHORT.map(d => (
            <div key={d} className={`text-center text-[10px] font-bold py-0.5 ${muted}`}>{d}</div>
          ))}
        </div>

        {/* Week rows */}
        {rows.map((row, ri) => {
          const firstReal = row.find(d => d !== null) as Date
          const rowMon    = isoMonday(firstReal)
          const isSel     = rowMon.getTime() === selMon.getTime()
          const isCurWeek = rowMon.getTime() === todayMon.getTime()

          return (
            <div
              key={ri}
              onClick={() => { onChange(weekOffsetFromDate(firstReal)); setOpen(false) }}
              className={`grid grid-cols-7 rounded cursor-pointer transition-colors mb-0.5 ${
                isSel
                  ? (dark ? 'bg-sky-500/25' : 'bg-sky-50')
                  : (dark ? 'hover:bg-white/6' : 'hover:bg-slate-50')
              } ${isCurWeek && !isSel ? (dark ? 'outline outline-1 outline-sky-500/40' : 'outline outline-1 outline-sky-200') : ''}`}
            >
              {row.map((d, ci) => {
                const isToday    = d !== null && d.getTime() === today.getTime()
                const isThisMonth = d !== null && d.getMonth() === calMonth
                return (
                  <div
                    key={ci}
                    className={`text-center py-1 text-[11px] rounded-sm ${
                      d === null         ? '' :
                      isToday            ? `font-black ${dark ? 'text-sky-400' : 'text-sky-500'}` :
                      !isThisMonth       ? muted :
                      isSel              ? `font-semibold ${dark ? 'text-sky-200' : 'text-sky-700'}` :
                                           txt
                    }`}
                  >
                    {d ? d.getDate() : ''}
                  </div>
                )
              })}
            </div>
          )
        })}

        <div className={`mt-2 pt-2 border-t ${bdrDiv} flex justify-center`}>
          <button
            onClick={() => { onChange(0); setOpen(false) }}
            className={`text-[11px] font-semibold px-3 py-0.5 rounded transition-colors ${
              dark ? 'text-sky-400 hover:bg-white/8' : 'text-sky-600 hover:bg-sky-50'
            }`}
          >Semana actual</button>
        </div>
      </div>
    )
  }

  function MonthPicker() {
    const { year: selYear, month: selMonth } = yearMonthFromOffset(value)
    const todayYear  = today.getFullYear()
    const todayMonth = today.getMonth()

    return (
      <div className="p-3 w-52">
        {/* Year nav */}
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setCalYear(y => y-1)} className={navBtn}>‹</button>
          <span className={`text-[12px] font-bold ${txt}`}>{calYear}</span>
          <button onClick={() => setCalYear(y => y+1)} className={navBtn}>›</button>
        </div>

        {/* 3×4 month grid */}
        <div className="grid grid-cols-3 gap-1">
          {MONTHS_SHORT.map((m, mi) => {
            const isSel   = calYear === selYear && mi === selMonth
            const isToday = calYear === todayYear && mi === todayMonth
            return (
              <button
                key={mi}
                onClick={() => { onChange(monthOffsetFromYM(calYear, mi)); setOpen(false) }}
                className={`py-1.5 rounded text-[11px] font-semibold transition-colors ${
                  isSel
                    ? 'bg-sky-500 text-white'
                    : isToday
                      ? (dark ? 'ring-1 ring-sky-400 text-sky-400' : 'ring-1 ring-sky-400 text-sky-600')
                      : (dark ? 'text-slate-300 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100')
                }`}
              >{m}</button>
            )
          })}
        </div>

        <div className={`mt-2 pt-2 border-t ${bdrDiv} flex justify-center`}>
          <button
            onClick={() => { onChange(0); setOpen(false) }}
            className={`text-[11px] font-semibold px-3 py-0.5 rounded transition-colors ${
              dark ? 'text-sky-400 hover:bg-white/8' : 'text-sky-600 hover:bg-sky-50'
            }`}
          >Mes actual</button>
        </div>
      </div>
    )
  }

  function YearPicker() {
    const selYear   = yearFromOffset(value)
    const todayYear = today.getFullYear()
    // Bloque de 12 años alineado a década, con `calYear` como ancla.
    const blockStart = Math.floor(calYear / 12) * 12

    return (
      <div className="p-3 w-52">
        {/* Navegación por bloques de 12 años */}
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setCalYear(y => y - 12)} className={navBtn}>‹</button>
          <span className={`text-[12px] font-bold ${txt}`}>{blockStart} – {blockStart + 11}</span>
          <button onClick={() => setCalYear(y => y + 12)} className={navBtn}>›</button>
        </div>

        {/* Rejilla 3×4 de años */}
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, i) => blockStart + i).map(yr => {
            const isSel   = yr === selYear
            const isToday = yr === todayYear
            return (
              <button
                key={yr}
                onClick={() => { onChange(yearOffsetFromYear(yr)); setOpen(false) }}
                className={`py-1.5 rounded text-[11px] font-semibold tabular-nums transition-colors ${
                  isSel
                    ? 'bg-sky-500 text-white'
                    : isToday
                      ? (dark ? 'ring-1 ring-sky-400 text-sky-400' : 'ring-1 ring-sky-400 text-sky-600')
                      : (dark ? 'text-slate-300 hover:bg-white/10' : 'text-slate-700 hover:bg-slate-100')
                }`}
              >{yr}</button>
            )
          })}
        </div>

        <div className={`mt-2 pt-2 border-t ${bdrDiv} flex justify-center`}>
          <button
            onClick={() => { onChange(0); setOpen(false) }}
            className={`text-[11px] font-semibold px-3 py-0.5 rounded transition-colors ${
              dark ? 'text-sky-400 hover:bg-white/8' : 'text-sky-600 hover:bg-sky-50'
            }`}
          >Año actual</button>
        </div>
      </div>
    )
  }

  const minW = mode === 'week' ? 'min-w-[96px]' : mode === 'day' ? 'min-w-[140px]' : 'min-w-[160px]'

  return (
    <div ref={ref} className="relative flex items-center gap-1">
      <button onClick={() => onChange(value - 1)} className={btnClass}>‹</button>
      <button
        onClick={() => setOpen(v => !v)}
        className={`${btnClass} ${minW} text-center ${open ? (dark ? 'bg-white/10' : 'bg-slate-100') : ''}`}
      >
        {label}
      </button>
      <button onClick={() => onChange(value + 1)} className={btnClass}>›</button>

      {open && (
        <div className={`${
          isMobile
            ? 'fixed left-1/2 -translate-x-1/2 top-[72px]'
            : 'absolute top-full mt-1.5 right-0 max-w-[calc(100vw-24px)]'
        } z-50 rounded-xl border shadow-xl ${popBg}`}>
          {mode === 'day' ? <DayPicker />
            : mode === 'week' ? <WeekPicker />
            : mode === 'year' ? <YearPicker />
            : <MonthPicker />}
        </div>
      )}
    </div>
  )
}
